import { ChangeDetectionStrategy, Component, inject, OnInit, SecurityContext } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { format, parseISO } from 'date-fns';
import { map, Observable } from 'rxjs';

import { ApiParams, CoreApiService, queryAll } from '../../../core-api.service';
import { getTaskStatusDisplay } from '../../../deprecated-tasks/tasks.utils';
import { LoggingService } from '../../../logging.service';
import { DryrunTaskDeletionPreview } from '../../../shared/models/deprecated-task.model';
import { Paginated } from '../../../shared/models/paginated.model';
import { User } from '../../../shared/models/user.model';

interface DeletePreviewRow extends Omit<DryrunTaskDeletionPreview, 'status'> {
  user: User;
  status: string;
}

@Component({
  host: {
    class: 'mat-dialog-component',
    'test-id': 'delete-task-preview-dialog',
  },
  templateUrl: './delete-task-preview-dialog.component.html',
  styleUrls: ['./delete-task-preview-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DeleteTaskPreviewDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<DeleteTaskPreviewDialogComponent>>(MatDialogRef);
  private coreApi = inject(CoreApiService);
  private logger = inject(LoggingService);
  private sanitizer = inject(DomSanitizer);
  data = inject<{
    protocolId: string;
    protocolLabel: string;
  }>(MAT_DIALOG_DATA);

  public isLoading = true;
  public totalRows = 0;
  public tasks$: Observable<DeletePreviewRow[]>;
  public tableColumns = ['label', 'container', 'assignee', 'dueDate', 'status'];
  public warningHTML: string = '';
  private users: User[] = [];

  ngOnInit(): void {
    queryAll(this.coreApi.users.query).subscribe({
      next: (users) => {
        this.users = users;
        this.loadPage(0);
      },
      error: (error) => {
        this.logger.error(error);
      },
    });
  }

  public confirm(): void {
    this.dialogRef.close(true);
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  public loadPage(index: number): void {
    const queryParams: ApiParams = {
      limit: 10,
    };
    if (index > 0) {
      queryParams.page = index + 1;
    }

    this.isLoading = true;
    this.tasks$ = this.coreApi.readTaskProtocols.deleteDryrun(this.data.protocolId, queryParams).pipe(
      map((tasks: Paginated<DryrunTaskDeletionPreview[]>) => {
        this.totalRows = tasks.total;
        const sanitizedLabel = this.sanitizer.sanitize(SecurityContext.HTML, this.data.protocolLabel);
        this.warningHTML =
          this.sanitizer.sanitize(
            SecurityContext.HTML,
            `Are you sure you want to delete <b>${sanitizedLabel || ''}</b>? There are <b>${this.totalRows} tasks</b> ` +
              'currently assigned to this protocol. Tasks must be associated with a protocol, so they will be deleted ' +
              'along with the protocol.',
          ) || '';
        this.isLoading = false;

        return tasks.results.map((task) => ({
          ...task,
          due_date: task.due_date ? format(parseISO(task.due_date), 'MMM dd, yyyy') : '',
          status: getTaskStatusDisplay(task.status),
          user: this.users.find((user) => user._id === task.assignee),
        }));
      }),
    );
  }
}
